import {Box, Button, Container, CssBaseline, Grid, Typography,} from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import React, {useState} from "react";
import {TextValidator, ValidatorForm} from "react-material-ui-form-validator";
import {connect} from "react-redux";
import {Link, Redirect} from "react-router-dom";

import logo from "../../../../assets/logo.svg";
import MessageDialog from "../../../../utilities/components/MessageDialog/MessageDialog";
import {
  APP_URLS,
  dxAIAdminMenu,
  orgAdminMenu,
  ROLES,
  superAdminMenu,
  userMenu,
  userMenuDoctor
} from "../../../../utilities/constants";
import {getUserInfo, isLoggedIn, setAccessToken, setUserInfo,} from "../../../../utilities/storage";
import {signInRequest} from "../../AuthApiActions";
import "./Login.css";

const Login = ({history, signIn, acceptAgreement}) => {
  const userInfo = getUserInfo();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showDisabledUserDialog, setShowDisabledUserDialog] = useState(false);
  const [showUnauthorizedOrgDialog, setShowUnauthorizedOrgDialog] = useState(false);
  const [showBannedUserDialog, setShowBannedUserDialog] = useState(false);
  const [bannedUserMessage, setBannedUserMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const getRouteAfterLogin = (role) => {
    let url = "";
    switch (role) {
      case ROLES.ROLE_SUPER_ADMIN:
        url = dxAIAdminMenu[0].path
        break;
      case ROLES.ROLE_SYSTEM_ADMIN:
        url = superAdminMenu[0].path
        break;
      case ROLES.ROLE_CLINIC_OWNER:
        url = superAdminMenu[0].path
        break;
      case ROLES.ROLE_ORG_ADMIN:
        url = orgAdminMenu[0].path
        break;
      case ROLES.ROLE_DOCTOR:
        url = userMenuDoctor[0].path
        break;
      case ROLES.ROLE_RECEPTIONIST:
      case ROLES.ROLE_PHARMACIST:
      case ROLES.ROLE_THERAPIST:
        url = userMenu[0].path
        break;
      default:
        break;
    }

    return url;
  };

  const onSubmit = (e) => {
    e.preventDefault();

    const data = {
      username: email,
      password: password,
    };

    signIn(data).then(
      (res) => {
        if (!res.user || !res.user.enabled) {
          setShowDisabledUserDialog(true);
          return;
        }
        if (!res.user.Roles || !Array.isArray(res.user.Roles) || res.user.Roles.length === 0) {
          return;
        }
        const userRole = res.user.Roles[0].name;

        if (userRole === "Admin") {
          let hasOrganization = false;
          if (res.user.Admin && Array.isArray(res.user.Admin.Organization)) {
            hasOrganization = res.user.Admin.Organization.length > 0;
          }

          if (!hasOrganization && Array.isArray(res.user.Organization)) {
            hasOrganization = res.user.Organization.length > 0;
          }

          // Show dialog and block login if no organizations
          if (!hasOrganization) {
            setShowUnauthorizedOrgDialog(true);
            return; // Critical - prevent further execution
          }
        }
        setUserInfo(res.user);
        setAccessToken(res.access_token);
        const redirectUrlBasedOnRole = getRouteAfterLogin(userRole);
        if (redirectUrlBasedOnRole) {
          history.push(redirectUrlBasedOnRole)
        }
      },
      (error) => {
        if (error?.response?.data?.error) {
          setBannedUserMessage(error.response.data.error);
          setShowBannedUserDialog(true);
        }
      }
    );
  };

  if (isLoggedIn()) {
    const userRole = userInfo && userInfo.Roles && userInfo.Roles.length > 0
      ? userInfo.Roles[0].name
      : "";

    if (userRole === "SystemAdmin") {
      return <Redirect to={APP_URLS.ORGANIZATIONS}/>;
    } else {
      const redirectUrlBasedOnRole = getRouteAfterLogin(userRole);
      if (redirectUrlBasedOnRole) {
        return <Redirect to={redirectUrlBasedOnRole}/>;
      }
    }
  }

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline/>
      <MessageDialog
        open={showDisabledUserDialog}
        setOpen={setShowDisabledUserDialog}
        title="Account Disabled"
        message="Your account has been temporarily locked out. Please contact support."
      />

      <MessageDialog
        open={showUnauthorizedOrgDialog}
        setOpen={setShowUnauthorizedOrgDialog}
        title="Authorization Required"
        message="You are not authorized by any organization. Please contact an administrator to be assigned to an organization."
      />

      <MessageDialog
        open={showBannedUserDialog}
        setOpen={setShowBannedUserDialog}
        title="Account Issue"
        message={bannedUserMessage}
      />

      <Box className="login-form-container">
        <img height={150} src={logo} alt="logo"/>
        <Typography component="h1" variant="h5" style={{marginTop: "1.5rem"}}>
          Sign in
        </Typography>

        <ValidatorForm noValidate className="login-form" onSubmit={onSubmit}>
          <TextValidator
            variant="outlined"
            margin="normal"
            fullWidth
            label="Email"
            validators={["required", "isEmail"]}
            errorMessages={["Email is required", "Email is not valid"]}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <TextValidator
            variant="outlined"
            margin="normal"
            fullWidth
            label="Password"
            type={showPassword ? "text" : "password"}
            validators={["required"]}
            errorMessages={["Password is required"]}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <Visibility/> : <VisibilityOff/>}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            className="login-button"
          >
            Sign In
          </Button>

          <Grid container>
            <Grid item xs>
              <Typography
                color="primary"
                component={Link}
                to={APP_URLS.FORGOT_PASSWORD}
                className="login-forget-password"
              >
                Forgot password?
              </Typography>
            </Grid>
          </Grid>
        </ValidatorForm>
      </Box>
    </Container>
  );
};

const mapStateToProps = (state) => {
  return {
    loggedInUser: state.auth.loggedInUser,
  };
};

const mapDispatchToProp = (dispatch) => {
  return {
    signIn: (data) => {
      return dispatch(signInRequest(data)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProp)(Login);
